import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../api";
import { PageHeader, Card, Spinner, Empty } from "../ui";
import { Gauge, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function fmt(n) {
  return (n == null ? 0 : n).toLocaleString("ro-RO");
}

function todayStr() {
  const d = new Date();
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function statusStyle(s) {
  const v = (s || "").toLowerCase();
  if (v === "transmite") return { color: "#0a7d28", border: "#0a7d28" };
  if (v === "suspendata") return { color: "#b45309", border: "#b45309" };
  if (v === "anulata" || v === "ciorna") return { color: "#6b7280", border: "#6b7280" };
  return { color: "#000", border: "#9ca3af" };
}

export default function Contor() {
  const [items, setItems] = useState([]);
  const [online, setOnline] = useState(true);
  const [updated, setUpdated] = useState("");
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(todayStr());
  const [toDate, setToDate] = useState(todayStr());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contor", {
        params: { from_date: fromDate, to_date: toDate },
      });
      setItems(data.items || []);
      setOnline(!!data.delivery_source_online);
      setUpdated(data.updated_at || "");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);
  useEffect(() => { load(); }, [load]);

  const setToday = () => { setFromDate(todayStr()); setToDate(todayStr()); };

  const cell = "px-3 py-2 border-b border-gray-200 text-black align-top";
  const num = cell + " text-right whitespace-nowrap";
  const head = "px-3 py-2 border-b-2 border-gray-400 font-bold text-black text-left whitespace-nowrap";
  const headNum = head + " text-right";

  const totals = items.reduce((a, c) => ({
    trimise: a.trimise + (c.trimise || 0),
    setat: a.setat + (c.setat || 0),
    rest: a.rest + (c.rest || 0),
  }), { trimise: 0, setat: 0, rest: 0 });

  return (
    <div style={{ fontFamily: "Arial", fontSize: "14px" }}>
      <PageHeader title="CONTOR" subtitle="Trimise fata de cate au fost setate (rampa) pe zi, per campanie. Implicit ziua curenta. Doar super admin." />

      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="contor-daterange">
        <div className="flex flex-col">
          <label className="text-gray-500 mb-1">De la data</label>
          <input type="date" value={fromDate} max={toDate || undefined}
            onChange={(e) => setFromDate(e.target.value)} data-testid="contor-from-date"
            className="px-3 py-2 rounded border border-gray-300 outline-none focus:border-black text-black"
            style={{ fontFamily: "Arial", fontSize: "14px" }} />
        </div>
        <div className="flex flex-col">
          <label className="text-gray-500 mb-1">Pana la data</label>
          <input type="date" value={toDate} min={fromDate || undefined}
            onChange={(e) => setToDate(e.target.value)} data-testid="contor-to-date"
            className="px-3 py-2 rounded border border-gray-300 outline-none focus:border-black text-black"
            style={{ fontFamily: "Arial", fontSize: "14px" }} />
        </div>
        <button onClick={setToday} data-testid="contor-today-btn"
          className="px-3 py-2 rounded border border-gray-300 text-black hover:bg-gray-100">
          Azi
        </button>
        <button onClick={load} data-testid="contor-refresh-btn"
          className="px-3 py-2 rounded bg-black text-white hover:bg-gray-800 inline-flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Actualizare
        </button>
      </div>

      {!online && (
        <div className="flex items-start gap-2 border border-gray-400 bg-gray-50 p-3 rounded mb-4" data-testid="contor-offline-note">
          <AlertTriangle className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
          <div className="text-gray-700">
            Cifra Trimise nu a putut fi citita de pe serverul de campanii. Se afiseaza doar valorile setate (rampa).
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4" data-testid="contor-totals">
            {[["Trimise", totals.trimise, "trimise"], ["Setat", totals.setat, "setat"], ["Rest", totals.rest, "rest"]].map(([label, val, tid]) => (
              <div key={tid} className="border border-gray-300 rounded p-3" data-testid={`contor-total-${tid}`}>
                <div className="text-gray-500">{label}</div>
                <div className="text-black font-bold" style={{ fontSize: "16px" }}>{fmt(val)}</div>
              </div>
            ))}
          </div>

          {items.length === 0 ? (
            <Card><Empty icon={Gauge} title="Nicio campanie" hint="Nu exista campanii create inca." /></Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full whitespace-nowrap" data-testid="contor-table" style={{ fontSize: "14px" }}>
                  <thead>
                    <tr>
                      <th className={head}>Tenant</th>
                      <th className={head}>Campanie</th>
                      <th className={headNum}>Trimise</th>
                      <th className={headNum}>Rest</th>
                      <th className={head}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c, i) => {
                      const st = statusStyle(c.status);
                      return (
                        <tr key={c.id || i} className="hover:bg-gray-50" data-testid={`contor-row-${i}`}>
                          <td className={cell}>{c.tenant || "-"}</td>
                          <td className={cell + " font-bold"} data-testid={`contor-campanie-${i}`}>{c.campanie || "-"}</td>
                          <td className={num + " font-bold"} data-testid={`contor-trimise-${i}`}>
                            {fmt(c.trimise)}<span className="text-gray-400 font-normal"> / {fmt(c.setat)}</span>
                          </td>
                          <td className={num + " font-bold"} data-testid={`contor-rest-${i}`}>{fmt(c.rest)}</td>
                          <td className={cell}>
                            <span data-testid={`contor-status-${i}`}
                              style={{ color: st.color, border: `1px solid ${st.border}`, padding: "2px 8px", borderRadius: "4px", fontWeight: "bold", fontSize: "12px" }}>
                              {c.status || "-"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <div className="mt-3 text-gray-500" data-testid="contor-updated">
            {updated ? `Actualizat: ${new Date(updated).toLocaleString("ro-RO")}` : ""}
          </div>
        </>
      )}
    </div>
  );
}
